import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AUTH_STORAGE_KEY, SERVER_URL } from "../socket";

/**
 * JoinGroupPage
 * Route: /join-group/:groupId
 *
 * Handles three states:
 *  1. Not logged in  → show preview + redirect to login (with returnUrl)
 *  2. Logged in      → fetch group info, show preview + "Join" button
 *  3. Already member → show "Open Chat" button
 */
export default function JoinGroupPage() {
  const { groupId }  = useParams();
  const navigate     = useNavigate();

  const [status,    setStatus]    = useState("loading"); // loading | preview | already | joined | error
  const [groupInfo, setGroupInfo] = useState(null);
  const [joining,   setJoining]   = useState(false);
  const [errorMsg,  setErrorMsg]  = useState("");

  /* ─── derive auth ─────────────────────────────────────────── */
  const raw     = localStorage.getItem(AUTH_STORAGE_KEY);
  const parsed  = raw ? JSON.parse(raw) : null;
  const token   = parsed?.token;
  const isLoggedIn = !!token;

  /* ─── fetch group preview (works even without token for public groups) */
  useEffect(() => {
    const load = async () => {
      try {
        const headers = { "Content-Type": "application/json" };
        if (token) headers.Authorization = `Bearer ${token}`;

        const res  = await fetch(`${SERVER_URL}/api/groups/preview/${groupId}`, { headers });
        const body = await res.json();

        if (!res.ok) {
          setErrorMsg(body.error || "Group not found or invite link is invalid.");
          setStatus("error");
          return;
        }

        setGroupInfo(body);
        setStatus(body.isMember ? "already" : "preview");
      } catch {
        setErrorMsg("Could not reach the server. Check your connection.");
        setStatus("error");
      }
    };
    load();
  }, [groupId, token]);

  /* ─── join ────────────────────────────────────────────────── */
  const handleJoin = async () => {
    if (!isLoggedIn) {
      // Save destination and redirect to login
      localStorage.setItem("joinRedirect", `/join-group/${groupId}`);
      navigate("/login");
      return;
    }

    setJoining(true);
    try {
      const res  = await fetch(`${SERVER_URL}/api/groups/join-via-link`, {
        method : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization : `Bearer ${token}`,
        },
        body: JSON.stringify({ groupId }),
      });
      const body = await res.json();

      if (body.success) {
        setStatus("joined");
        // Give user a moment to see the success state, then go to the chat app
        setTimeout(() => navigate(`/kmail/krivachat?group=${groupId}`, { state: { groupId } }), 1800);
      } else {
        setErrorMsg(body.error || "Failed to join group.");
        setStatus("error");
      }
    } catch {
      setErrorMsg("Something went wrong. Please try again.");
      setStatus("error");
    } finally {
      setJoining(false);
    }
  };

  /* ─── avatar helpers ──────────────────────────────────────── */
  const avatarColors = ["#1e40af","#0f766e","#7c3aed","#b45309","#be123c","#166534"];
  const colorFor = (s) => avatarColors[(s?.charCodeAt(0) || 0) % avatarColors.length];

  /* ════════════════════════════════════════════════════════════
      Render
     ════════════════════════════════════════════════════════════ */
  return (
    <div style={styles.root}>
      {/* ── Animated background blobs ── */}
      <div style={styles.blob1} />
      <div style={styles.blob2} />

      <div style={styles.card}>
        {/* ── App branding ── */}
        <div style={styles.appBadge}>
          <span style={styles.appDot} />
          Kriva Chat
        </div>

        {/* ─────────── LOADING ─────────── */}
        {status === "loading" && (
          <div style={styles.centerCol}>
            <div style={styles.spinner} />
            <p style={styles.mutedText}>Fetching group info…</p>
          </div>
        )}

        {/* ─────────── ERROR ─────────── */}
        {status === "error" && (
          <div style={styles.centerCol}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🔗</div>
            <h2 style={styles.title}>Invalid invite link</h2>
            <p style={{ ...styles.mutedText, maxWidth: 280, textAlign: "center" }}>
              {errorMsg}
            </p>
            <button onClick={() => navigate("/")} style={styles.primaryBtn}>
              Go home
            </button>
          </div>
        )}

        {/* ─────────── SUCCESS ─────────── */}
        {status === "joined" && (
          <div style={styles.centerCol}>
            <div style={styles.successCircle}>✓</div>
            <h2 style={{ ...styles.title, marginTop: 20 }}>You're in!</h2>
            <p style={styles.mutedText}>Taking you to the group…</p>
          </div>
        )}

        {/* ─────────── PREVIEW / ALREADY ─────────── */}
        {(status === "preview" || status === "already") && groupInfo && (
          <>
            {/* Group avatar */}
            <div style={{
              ...styles.groupAvatar,
              background: groupInfo.groupImage
                ? "#000"
                : colorFor(groupInfo.name),
            }}>
              {groupInfo.groupImage
                ? <img
                    src={`${SERVER_URL}${groupInfo.groupImage}`}
                    alt={groupInfo.name}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                : (groupInfo.name || "G")[0].toUpperCase()
              }
            </div>

            {/* Group name */}
            <h1 style={styles.groupName}>{groupInfo.name}</h1>

            {/* Meta pill */}
            <div style={styles.metaPill}>
              <span>👥 {groupInfo.memberCount} member{groupInfo.memberCount !== 1 ? "s" : ""}</span>
              {groupInfo.onlyAdmin && (
                <>
                  <span style={styles.pillDivider}>·</span>
                  <span>🔒 Admins only</span>
                </>
              )}
            </div>

            {/* Description (if any) */}
            {groupInfo.description && (
              <p style={styles.description}>{groupInfo.description}</p>
            )}

            {/* Divider */}
            <div style={styles.divider} />

            {/* Member preview avatars */}
            {groupInfo.previewMembers?.length > 0 && (
              <div style={styles.memberPreview}>
                <div style={styles.avatarStack}>
                  {groupInfo.previewMembers.slice(0, 5).map((uid, i) => (
                    <div
                      key={uid}
                      title={uid.split("@")[0]}
                      style={{
                        ...styles.miniAvatar,
                        background: colorFor(uid),
                        marginLeft: i === 0 ? 0 : -10,
                        zIndex: 10 - i,
                      }}
                    >
                      {uid[0].toUpperCase()}
                    </div>
                  ))}
                  {groupInfo.memberCount > 5 && (
                    <div style={{ ...styles.miniAvatar, background: "#1e3a5f", marginLeft: -10, fontSize: 10 }}>
                      +{groupInfo.memberCount - 5}
                    </div>
                  )}
                </div>
                <span style={styles.mutedText}>
                  {groupInfo.previewMembers
                    .slice(0, 2)
                    .map(u => u.split("@")[0])
                    .join(", ")}
                  {groupInfo.memberCount > 2 ? ` and ${groupInfo.memberCount - 2} others` : ""}
                </span>
              </div>
            )}

            {/* CTA */}
            {status === "already" ? (
              <button
                onClick={() => navigate(`/kmail/krivachat?group=${groupId}`, { state: { groupId } })}
                style={styles.primaryBtn}
              >
                Open Chat →
              </button>
            ) : !isLoggedIn ? (
              <>
                <button onClick={handleJoin} style={styles.primaryBtn}>
                  Sign in to Join
                </button>
                <p style={{ ...styles.mutedText, marginTop: 10, fontSize: 11 }}>
                  You'll be added automatically after signing in.
                </p>
              </>
            ) : (
              <button
                onClick={handleJoin}
                disabled={joining}
                style={{ ...styles.primaryBtn, opacity: joining ? 0.7 : 1 }}
              >
                {joining ? "Joining…" : "Join Group"}
              </button>
            )}

            {/* Invite note */}
            <p style={styles.footerNote}>
              You were invited via a group link
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Styles ──────────────────────────────────────────────────── */
const styles = {
  root: {
    minHeight    : "100vh",
    background   : "#050505",
    display      : "flex",
    alignItems   : "center",
    justifyContent: "center",
    padding      : "20px 16px",
    position     : "relative",
    overflow     : "hidden",
    fontFamily   : "'Inter', system-ui, sans-serif",
  },
  blob1: {
    position     : "absolute",
    top          : "-120px",
    left         : "-120px",
    width        : "400px",
    height       : "400px",
    borderRadius : "50%",
    background   : "radial-gradient(circle, rgba(245,112,17,0.18) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  blob2: {
    position     : "absolute",
    bottom       : "-100px",
    right        : "-100px",
    width        : "340px",
    height       : "340px",
    borderRadius : "50%",
    background   : "radial-gradient(circle, rgba(30,64,175,0.22) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  card: {
    position      : "relative",
    zIndex        : 1,
    width         : "100%",
    maxWidth      : "420px",
    background    : "rgba(10,22,40,0.92)",
    border        : "1px solid #1e3a5f",
    borderRadius  : "24px",
    padding       : "36px 28px 28px",
    display       : "flex",
    flexDirection : "column",
    alignItems    : "center",
    backdropFilter: "blur(12px)",
    boxShadow     : "0 32px 80px rgba(0,0,0,0.6)",
  },
  appBadge: {
    display       : "flex",
    alignItems    : "center",
    gap           : "6px",
    color         : "#f57011",
    fontSize      : "12px",
    fontWeight    : "700",
    letterSpacing : "1px",
    textTransform : "uppercase",
    marginBottom  : "28px",
  },
  appDot: {
    width        : "8px",
    height       : "8px",
    borderRadius : "50%",
    background   : "#f57011",
    display      : "block",
  },
  groupAvatar: {
    width        : "88px",
    height       : "88px",
    borderRadius : "50%",
    border       : "3px solid #f57011",
    display      : "flex",
    alignItems   : "center",
    justifyContent: "center",
    fontSize     : "36px",
    fontWeight   : "700",
    color        : "#fff",
    overflow     : "hidden",
    marginBottom : "16px",
    flexShrink   : 0,
    boxShadow    : "0 0 0 6px rgba(245,112,17,0.12)",
  },
  groupName: {
    color       : "#f1f5f9",
    fontSize    : "22px",
    fontWeight  : "800",
    margin      : "0 0 10px",
    textAlign   : "center",
    lineHeight  : 1.25,
  },
  metaPill: {
    display         : "flex",
    alignItems      : "center",
    gap             : "6px",
    background      : "rgba(30,42,66,0.8)",
    border          : "1px solid #1e3a5f",
    borderRadius    : "20px",
    padding         : "5px 14px",
    color           : "#94a3b8",
    fontSize        : "12px",
    fontWeight      : "600",
    marginBottom    : "14px",
  },
  pillDivider: {
    color: "#334155",
  },
  description: {
    color       : "#64748b",
    fontSize    : "13px",
    textAlign   : "center",
    margin      : "0 0 16px",
    lineHeight  : 1.5,
    maxWidth    : "300px",
  },
  divider: {
    width       : "100%",
    height      : "1px",
    background  : "#1e3a5f",
    margin      : "8px 0 18px",
  },
  memberPreview: {
    display         : "flex",
    flexDirection   : "column",
    alignItems      : "center",
    gap             : "8px",
    marginBottom    : "22px",
  },
  avatarStack: {
    display     : "flex",
    alignItems  : "center",
  },
  miniAvatar: {
    width           : "32px",
    height          : "32px",
    borderRadius    : "50%",
    border          : "2px solid #0a1628",
    display         : "flex",
    alignItems      : "center",
    justifyContent  : "center",
    fontSize        : "13px",
    fontWeight      : "700",
    color           : "#fff",
    flexShrink      : 0,
  },
  primaryBtn: {
    width           : "100%",
    border          : "none",
    borderRadius    : "12px",
    padding         : "14px 0",
    fontSize        : "15px",
    fontWeight      : "700",
    cursor          : "pointer",
    color           : "#fff",
    background      : "linear-gradient(135deg, #ea6000, #f57011)",
    boxShadow       : "0 4px 20px rgba(245,112,17,0.3)",
    transition      : "transform 0.15s, box-shadow 0.15s",
    marginTop       : "4px",
  },
  footerNote: {
    color       : "#334155",
    fontSize    : "11px",
    marginTop   : "14px",
    textAlign   : "center",
  },
  centerCol: {
    display         : "flex",
    flexDirection   : "column",
    alignItems      : "center",
    padding         : "20px 0",
    gap             : "12px",
    width           : "100%",
  },
  title: {
    color       : "#f1f5f9",
    fontSize    : "20px",
    fontWeight  : "700",
    margin      : 0,
    textAlign   : "center",
  },
  mutedText: {
    color       : "#64748b",
    fontSize    : "13px",
    margin      : 0,
    textAlign   : "center",
  },
  spinner: {
    width           : "40px",
    height          : "40px",
    borderRadius    : "50%",
    border          : "3px solid #1e3a5f",
    borderTop       : "3px solid #f57011",
    animation       : "spin 0.8s linear infinite",
  },
  successCircle: {
    width           : "72px",
    height          : "72px",
    borderRadius    : "50%",
    background      : "linear-gradient(135deg,#064e3b,#065f46)",
    border          : "2px solid #34d399",
    display         : "flex",
    alignItems      : "center",
    justifyContent  : "center",
    fontSize        : "30px",
    color           : "#34d399",
    boxShadow       : "0 0 0 8px rgba(52,211,153,0.1)",
  },
};

/* inject spinner keyframes once */
if (typeof document !== "undefined" && !document.getElementById("join-spin-style")) {
  const s = document.createElement("style");
  s.id = "join-spin-style";
  s.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(s);
}
